/**
 * StatsCard - Dashboard statistics card
 *
 * Core component: No internal dependencies.
 * Mobile-first, accessible design with color variants.
 */

import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StatsCardProps {
  /** Card title */
  title: string;
  /** Main value to display */
  value: string | number;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional icon */
  icon?: LucideIcon;
  /** Color variant */
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  /** Optional trend indicator */
  trend?: {
    value: number;
    label?: string;
  };
  /** Loading state */
  loading?: boolean;
  /** Additional class names */
  className?: string;
}

// iOS-inspired colors - white cards with colored accents
const variantStyles: Record<string, {
  card: string;
  icon: string;
  title: string;
  value: string;
  subtitle: string;
}> = {
  default: {
    card: 'bg-white',
    icon: 'text-slate-500 bg-slate-100',
    title: 'text-slate-500',
    value: 'text-slate-800',
    subtitle: 'text-slate-400',
  },
  success: {
    card: 'bg-white',
    icon: 'text-emerald-600 bg-emerald-50',
    title: 'text-slate-500',
    value: 'text-emerald-600',
    subtitle: 'text-slate-400',
  },
  warning: {
    card: 'bg-white',
    icon: 'text-amber-600 bg-amber-50',
    title: 'text-slate-500',
    value: 'text-amber-600',
    subtitle: 'text-slate-400',
  },
  error: {
    card: 'bg-white',
    icon: 'text-rose-500 bg-rose-50',
    title: 'text-slate-500',
    value: 'text-rose-600',
    subtitle: 'text-slate-400',
  },
  info: {
    card: 'bg-white',
    icon: 'text-sky-500 bg-sky-50',
    title: 'text-slate-500',
    value: 'text-sky-600',
    subtitle: 'text-slate-400',
  },
};

// Loading dots animation
function LoadingDots() {
  return (
    <div className="flex items-center gap-1">
      <span className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.3s]" />
      <span className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.15s]" />
      <span className="w-2 h-2 bg-current rounded-full animate-bounce" />
    </div>
  );
}

export function StatsCard({
  title,
  value,
  subtitle,
  icon: Icon,
  variant = 'default',
  trend,
  loading = false,
  className,
}: StatsCardProps) {
  const styles = (variantStyles[variant] ?? variantStyles.default)!;

  return (
    <div className={cn(
      'p-4 rounded-2xl',
      'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
      styles.card,
      className
    )}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          {/* Title */}
          <p className={cn('text-sm font-medium truncate', styles.title)}>
            {title}
          </p>

          {/* Value or Loading */}
          <div className={cn('text-2xl font-bold', styles.value)}>
            {loading ? (
              <LoadingDots />
            ) : (
              typeof value === 'number' ? value.toLocaleString() : value
            )}
          </div>

          {/* Subtitle and Trend */}
          {(subtitle || trend) && !loading && (
            <div className="flex items-center gap-2 flex-wrap">
              {subtitle && (
                <span className={cn('text-xs', styles.subtitle)}>
                  {subtitle}
                </span>
              )}
              {trend && (
                <span
                  className={cn(
                    'text-xs font-medium',
                    trend.value >= 0
                      ? 'text-emerald-500'
                      : 'text-rose-500'
                  )}
                >
                  {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}%
                  {trend.label && ` ${trend.label}`}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Icon */}
        {Icon && !loading && (
          <div className={cn('p-2.5 rounded-lg flex-shrink-0', styles.icon)}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
}
